import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, LayoutDashboard, LogOut, Menu, UserRound, X } from "lucide-react";
import { SunkevLogo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/craftsi", label: "Craftsi" },
  { to: "/businesses", label: "Our Businesses" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact Us" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-500 ${
        scrolled
          ? "border-gray-100 bg-white/85 py-3 shadow-sm backdrop-blur-xl"
          : "border-transparent bg-white/60 py-5 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" aria-label="Sunkev Enterprises Private Limited — Home">
          <SunkevLogo compact={scrolled} />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `gold-underline text-sm font-medium tracking-wide transition-colors duration-300 ${
                  isActive ? "active text-navy" : "text-fog hover:text-navy"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-5 lg:flex">
          {user === null ? null : user ? (
            <div className="group relative">
              <button
                data-testid="account-menu-button"
                aria-label="Account menu"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-navy text-white transition-colors duration-300 hover:bg-navy-800"
              >
                <UserRound className="h-4 w-4" />
              </button>
              <div className="invisible absolute right-0 top-12 w-56 rounded-2xl border border-gray-100 bg-white p-2 opacity-0 shadow-xl transition-all duration-300 group-hover:visible group-hover:opacity-100">
                <p className="truncate px-3 py-2 text-xs text-fog">{user.email}</p>
                {["admin", "member"].includes(user.role) && (
                  <Link
                    to="/admin"
                    data-testid="menu-admin"
                    className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium text-ink transition-colors duration-200 hover:bg-mist"
                  >
                    <LayoutDashboard className="h-4 w-4 text-royal" /> Enquiries Inbox
                  </Link>
                )}
                <button
                  onClick={logout}
                  data-testid="menu-logout"
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium text-red-600 transition-colors duration-200 hover:bg-red-50"
                >
                  <LogOut className="h-4 w-4" /> Sign Out
                </button>
              </div>
            </div>
          ) : null}
          <Link
            to="/contact"
            data-testid="header-get-in-touch"
            className="group inline-flex items-center gap-2 rounded-full bg-navy px-6 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-navy-800 hover:shadow-lg hover:shadow-navy/25"
          >
            Get in Touch
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 text-navy lg:hidden"
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-gray-100 bg-white lg:hidden"
            aria-label="Mobile"
          >
            <div className="space-y-1 px-5 py-6">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `block rounded-xl px-4 py-3 font-heading text-lg font-semibold ${
                        isActive ? "bg-mist text-navy" : "text-ink"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-get-in-touch"
                className="mt-4 flex items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 font-semibold text-white"
              >
                Get in Touch <ArrowRight className="h-4 w-4" />
              </Link>
              {user ? (
                <div className="mt-3 space-y-1">
                  {["admin", "member"].includes(user.role) && (
                    <NavLink
                      to="/admin"
                      data-testid="mobile-nav-admin"
                      className="flex items-center gap-2 rounded-xl px-4 py-3 font-heading text-base font-semibold text-ink"
                    >
                      <LayoutDashboard className="h-4 w-4 text-royal" /> Enquiries Inbox
                    </NavLink>
                  )}
                  <button
                    onClick={logout}
                    data-testid="mobile-logout"
                    className="flex w-full items-center gap-2 rounded-xl px-4 py-3 text-left font-heading text-base font-semibold text-red-600"
                  >
                    <LogOut className="h-4 w-4" /> Sign Out
                  </button>
                </div>
              ) : null}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
